import BRAND_COLORS from "./brand-colors.json";
import determineTextColor from "./determine-text-color";

export interface ThemeParams {
  app_look?: string;
  card_style?: string;
  default_color_mode?: string;
  heading_font?: string | null;
  body_font?: string | null;
  primary_color?: { hex: string; rgb: string };
  secondary_color?: { hex: string; rgb: string };
  secondary_2_color?: { hex: string; rgb: string };
  secondary_3_color?: { hex: string; rgb: string };
  light_color?: { hex: string; rgb: string };
  dark_color?: { hex: string; rgb: string };
  brand_color?: { hex: string; rgb: string };
  global_css?: string | null;
  global_head_code?: string | null;
  global_body_code?: string | null;
  show_fitter_badge?: boolean;
}

export interface ThemeOptions {
  [key: string]: string;
}

export const SYSTEM_FONT_STACK = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", Helvetica, Arial, sans-serif';

export const APP_COLOR_HEX: Record<string, string> = {
  "BLACK": "#000000",
  "WHITE": "#ffffff",
  "LIGHT_GREY": "#dde3e9",
  "GREY": "#8795a8"
};

const THEME_DEFAULTS = {
  "--button-text-color-hex": "#fff",
  "--border-radius": "6px",
  "--input-border-radius": "12px",
  "--card-shadow": "0 2px 5px 0 rgba(60, 66, 87, 0.12), 0 1px 1px 0 rgba(0, 0, 0, 0.12)",
  "--body-font-stack": "-apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, Oxygen-Sans, Ubuntu, Cantarell, \"Helvetica Neue\", sans-serif",
  "--heading-font-stack": "-apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, Oxygen-Sans, Ubuntu, Cantarell, \"Helvetica Neue\", sans-serif"
};

const CARD_STYLE_VALUES = {
  "raised": {
    "--card-shadow": "0 4px 8px rgba(0, 0, 0, 0.15)"
  },
  "flat": {
    "--card-shadow": "0 0 0 1px rgba(0, 0, 0, 0.1)"
  }
};

const APP_LOOK_VALUES = {
  "rounded": {
    "--input-border-radius": "24px",
    "--border-radius": "16px"
  },
  "square": {
    "--input-border-radius": "3px",
    "--border-radius": "3px"
  }
};

// --- Theme Logic ---

function rgbToVals(rgb: string): string {
  return rgb.replace(/rgb\(/, "").replace(")", "");
}

export abstract class BaseTheme {
  options: ThemeOptions = {};
  showFitterBadge?: boolean;

  constructor() {
    this.initializeDefaults();
    this.showFitterBadge = false;
  }

  protected initializeDefaults(): void {
    // Initialize with theme defaults
    Object.keys(THEME_DEFAULTS).forEach((key) => {
      this.options[key] = THEME_DEFAULTS[key as keyof typeof THEME_DEFAULTS];
    });

    // Set default brand colors
    Object.values(BRAND_COLORS).forEach((color: any) => {
      this.options[color.hex_css_val] = color.hex_default;
      this.options[color.rgb_css_val] = color.rgb_default;
      this.options[color.text_hex_css_val] = determineTextColor(color.hex_default);
    });
  }

  set(key: string, value: string): void {
    this.options[key] = value;
  }

  get(key: string): string | undefined {
    return this.options[key];
  }

  getOptions(): ThemeOptions {
    return { ...this.options };
  }

  applyThemeParams(themeParams: ThemeParams, additionalOptions?: {
    scssVariables?: any;
  }): void {
    this.showFitterBadge = themeParams.show_fitter_badge;

    // Apply app_look settings
    if (themeParams.app_look) {
      const appLookValues = APP_LOOK_VALUES[themeParams.app_look as keyof typeof APP_LOOK_VALUES];
      if (appLookValues) {
        Object.keys(appLookValues).forEach((key) => {
          this.set(key, appLookValues[key as keyof typeof appLookValues]);
        });
      }
    }

    // Apply card_style settings
    if (themeParams.card_style) {
      const cardStyleValues = CARD_STYLE_VALUES[themeParams.card_style as keyof typeof CARD_STYLE_VALUES];
      if (cardStyleValues) {
        Object.keys(cardStyleValues).forEach((key) => {
          this.set(key, cardStyleValues[key as keyof typeof cardStyleValues]);
        });
      } else if (additionalOptions?.scssVariables) {
        // Fallback to scssVariables for PWA theme
        const scssVarMap: Record<string, string> = {
          raised: "box-shadow--large",
          flat: "box-shadow--keyline",
        };
        const scssVar = scssVarMap[themeParams.card_style];
        if (scssVar) {
          this.set("--card-shadow", additionalOptions.scssVariables[scssVar]);
        }
      }
    }

    // Apply fonts
    if (themeParams.body_font) {
      this.set("--body-font-stack", `"${themeParams.body_font}", ${SYSTEM_FONT_STACK}`);
    } else {
      this.set("--body-font-stack", THEME_DEFAULTS["--body-font-stack"]);
    }

    if (themeParams.heading_font) {
      this.set("--heading-font-stack", `"${themeParams.heading_font}", ${SYSTEM_FONT_STACK}`);
    } else {
      this.set("--heading-font-stack", THEME_DEFAULTS["--heading-font-stack"]);
    }

    // Apply primary color button text color
    if (themeParams.primary_color) {
      const textColor = determineTextColor(
        themeParams.primary_color.hex,
        additionalOptions?.scssVariables ? {
          black: additionalOptions.scssVariables["color-ui--grey-80"],
          white: "#fff"
        } : undefined
      );
      this.set("--button-text-color-hex", textColor);
    }

    // Apply brand colors from theme
    Object.values(BRAND_COLORS).forEach((color: any) => {
      const themeColor = themeParams[color.field as keyof ThemeParams] as { hex: string; rgb: string } | undefined;
      if (themeColor) {
        this.set(color.hex_css_val, themeColor.hex);
        this.set(color.rgb_css_val, rgbToVals(themeColor.rgb));
        this.set(color.text_hex_css_val, determineTextColor(themeColor.hex));
      }
    });

    // Preserve legacy brand_color (primary_color)
    if (themeParams.brand_color || themeParams.primary_color) {
      const brandColor = themeParams.brand_color || themeParams.primary_color;
      if (brandColor) {
        const rgbVals = rgbToVals(brandColor.rgb);

        const textColor = determineTextColor(
          brandColor.hex,
          additionalOptions?.scssVariables ? {
            black: additionalOptions.scssVariables["color-ui--grey-80"],
            white: "#fff"
          } : undefined
        );

        this.set("--brand-color-hex", brandColor.hex);
        this.set("--brand-color-rgb-vals", rgbVals);
        this.set("--button-text-color-hex", textColor);
        this.set("--primary-brand-color-hex", brandColor.hex);
        this.set("--primary-brand-color-rgb-vals", rgbVals);
      }
    }
  }
  /**
   * Resolve brand color names or fixed color names to hex values
   */
  resolveColor(color: string | null | undefined): string {
    if (!color) {
      return ""; // Default fallback
    }

    // Fixed colors from shared constants
    const fixedColors: Record<string, string> = {
      white: APP_COLOR_HEX.WHITE,
      light_grey: APP_COLOR_HEX.LIGHT_GREY,
      grey: APP_COLOR_HEX.GREY,
      black: APP_COLOR_HEX.BLACK,
    };

    if (fixedColors[color]) {
      return fixedColors[color];
    }

    let colorKey = color;
    if (colorKey === 'brand') colorKey = 'primary';

    const brandColor = BRAND_COLORS[colorKey as keyof typeof BRAND_COLORS];
    if (brandColor) {
      return this.get(brandColor.hex_css_val) || "";
    }

    // If already a hex color, return it
    if (color.startsWith("#")) {
      return color;
    }

    return "";
  }
}
